import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

export default function PolicyProcedureDevelopmentService() {
  useSEO({
    title: 'Policy & Procedure Development for ACHC Accreditation | Advantixx',
    description: 'Custom ACHC-compliant policy and procedure manuals for home health agencies — clinical, administrative, HR, QAPI, and emergency preparedness policies.',
    keywords: 'ACHC policy procedure development, home health P&P manual, ACHC compliant policies, home health procedures',
    canonical: '/services/policy-procedure-development',
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />
      <section className="relative pt-36 pb-0">
        <div className="relative h-[380px] overflow-hidden">
          <img src="https://public.readdy.ai/ai/img_res/8760792822802f2f108e0844bb7a01dd.jpg" alt="Policy and Procedure Development Service" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-5xl mx-auto px-6 md:px-10 pb-10">
            <span className="inline-block bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-4">Core Service</span>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">Policy &amp; Procedure <span className="text-[#d4af37]">Development</span></h1>
          </div>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1 min-w-0">
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              Your policies and procedures are the foundation of ACHC accreditation. Advantixx develops complete, ACHC-compliant P&amp;P manuals tailored to your agency&apos;s specific services, state regulations, and operational needs.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
              {[
                { icon: 'ri-heart-pulse-line', title: 'Clinical Policies', desc: 'Skilled nursing, therapy, wound care, medication management, infection control, and all clinical service policies.' },
                { icon: 'ri-team-line', title: 'HR & Personnel Policies', desc: 'Hiring, credentialing, orientation, competency evaluation, performance management, and workplace safety.' },
                { icon: 'ri-file-list-3-line', title: 'Administrative Policies', desc: 'Admission/discharge, billing compliance, patient rights, HIPAA, clinical records, and grievance procedures.' },
                { icon: 'ri-shield-check-line', title: 'Emergency Preparedness', desc: 'Emergency operations plans, communication plans, patient tracking, and annual testing documentation.' },
                { icon: 'ri-bar-chart-line', title: 'QAPI Policies', desc: 'Quality assessment program design, performance improvement project templates, and data monitoring policies.' },
                { icon: 'ri-refresh-line', title: 'Annual Review Service', desc: 'Ongoing annual review and update of all policies to reflect regulatory changes and ACHC standard updates.' },
              ].map((item, i) => (
                <div key={i} className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 rounded-lg mb-4"><i className={`${item.icon} text-[#d4af37] text-lg`}></i></div>
                  <h3 className="text-white font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-[#777] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center">
              <h3 className="text-white font-black text-xl mb-3">Get Your Custom P&amp;P Manual</h3>
              <p className="text-[#888] text-sm mb-6">Fully compliant, ready-to-implement policies tailored to your agency.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Book Consultation <i className="ri-arrow-right-line"></i></Link>
            </div>
          </div>
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              <div className="bg-[#141414] border border-[#d4af37]/15 rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">All Services</p>
                <ul className="flex flex-col gap-3">
                  {[
                    { label: 'Accreditation Preparation', path: '/services/achc-accreditation-preparation' },
                    { label: 'Mock Survey Services', path: '/services/mock-survey' },
                    { label: 'Policy & Procedure Development', path: '/services/policy-procedure-development', active: true },
                    { label: 'Compliance Audits', path: '/services/compliance-audit' },
                    { label: 'Staff Training', path: '/services/staff-training' },
                    { label: 'Ongoing Compliance Support', path: '/services/ongoing-compliance-support' },
                  ].map((item, i) => (
                    <li key={i}><Link to={item.path} className={`flex items-center gap-2 text-sm transition-colors duration-200 cursor-pointer ${item.active ? 'text-[#d4af37] font-semibold' : 'text-[#888] hover:text-[#d4af37]'}`}><i className={`ri-arrow-right-s-line ${item.active ? 'text-[#d4af37]' : 'text-[#555]'}`}></i>{item.label}</Link></li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-phone-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Speak with Dr. Villarreal</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Expert guidance for your accreditation journey.</p>
                <Link to="/contact" className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer">Contact Us</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
